import type {Components, Theme} from '@mui/material/styles';

import {adjustAlpha} from '@famly/mf_colors';

/**
 * `small` is the desktop toggle and `medium` the mobile one, so the mobile thumb stays a
 * comfortable touch target.
 */
const dimensions = (size: 'small' | 'medium' | undefined) =>
    size === 'small' ? {width: 40, height: 24, thumb: 20, offset: 16} : {width: 48, height: 28, thumb: 24, offset: 20};

/** Ring drawn around the thumb on hover, outside the track. */
const HOVER_RING_WIDTH = 5;

const DISABLED_OPACITY = 0.5;

/**
 * The thumb needs a shadow that lifts it barely 1px off the track. No Modern Famly elevation is
 * this shallow: the lowest one drops 3px, which on a thumb this small reads as the whole control
 * floating.
 */
const THUMB_SHADOW = '0px 1px 2px 0px rgba(10, 13, 18, 0.06), 0px 1px 3px 0px rgba(10, 13, 18, 0.1)';

/**
 * The glyphs keep this size on both toggle sizes, so they are drawn on a fixed 16px canvas and
 * centred on the thumb rather than scaled with it. Stroke is 1.75, a step up from the 1.5 of the
 * 12px UI icon, so the mark reads on a thumb this small.
 */
const ICON_CANVAS = 16;
const ICON_STROKE_WIDTH = 1.75;

const icon = (path: string, color: string) =>
    `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_CANVAS}" height="${ICON_CANVAS}" viewBox="0 0 ${ICON_CANVAS} ${ICON_CANVAS}" fill="none"><path d="${path}" stroke="${encodeURIComponent(
        color,
    )}" stroke-width="${ICON_STROKE_WIDTH}" stroke-linecap="round" stroke-linejoin="round"/></svg>')`;

const CROSS_PATH = 'M4.7 4.7L11.3 11.3M11.3 4.7L4.7 11.3';
const CHECK_PATH = 'M4.5 8.3L6.9 10.7L11.5 5.5';

const cross = (color: string) => icon(CROSS_PATH, color);
const check = (color: string) => icon(CHECK_PATH, color);

/**
 * Every rule lives in the `root` slot: MUI declares its own `size` styles as descendant selectors
 * inside that slot, and a slot-level override loses to them on specificity.
 */
export const SwitchThemeConfiguration: Components<Theme>['MuiSwitch'] = {
    styleOverrides: {
        root: ({theme, ownerState: {size, checked, disabled}}) => {
            const {width, height, thumb, offset} = dimensions(size);
            const {colorPalette} = theme.modernFamlyTheme;

            const trackColor = checked ? colorPalette.p400 : colorPalette.n100;
            const iconColor = checked ? colorPalette.p400 : colorPalette.n300;

            // Hovering or focusing an off toggle tints its track towards the primary colour, at
            // the same lightness as the resting track so it reads as a response, not as fading out.
            const tintedTrack = checked ? undefined : {backgroundColor: colorPalette.p200};

            return {
                width,
                height,
                padding: 0,
                // The hover and focus rings are drawn outside the track.
                overflow: 'visible',
                // A disabled toggle keeps the colours of its state and dims as a whole, so on and
                // off stay distinguishable while the control reads as unavailable.
                opacity: disabled ? DISABLED_OPACITY : 1,

                '& .MuiSwitch-switchBase': {
                    padding: (height - thumb) / 2,

                    '&.Mui-checked': {
                        transform: `translateX(${offset}px)`,
                    },

                    '&:hover, &.Mui-checked:hover': {
                        backgroundColor: 'transparent',
                    },

                    '&.Mui-focusVisible + .MuiSwitch-track': {
                        outline: `2px solid ${colorPalette.p300}`,
                        ...tintedTrack,
                    },
                },

                '& .MuiSwitch-thumb': {
                    width: thumb,
                    height: thumb,
                    margin: 0,
                    boxSizing: 'border-box',
                    backgroundColor: colorPalette.n0,
                    boxShadow: THUMB_SHADOW,
                    backgroundImage: checked ? check(iconColor) : cross(iconColor),
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                },

                // MUI paints the checked and disabled track from its own palette through
                // `.MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track`, so the track needs a
                // selector of at least that weight to keep its Modern Famly colour.
                [[
                    '& .MuiSwitch-track',
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track',
                    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track',
                ].join(', ')]: {
                    backgroundColor: trackColor,
                    opacity: 1,
                    border: 'none',
                    borderRadius: height / 2,
                    boxSizing: 'border-box',
                },

                ...(disabled
                    ? undefined
                    : {
                          '&:hover': {
                              '& .MuiSwitch-thumb': {
                                  boxShadow: `${THUMB_SHADOW}, 0 0 0 ${HOVER_RING_WIDTH}px ${adjustAlpha(
                                      checked ? 0.12 : 0.16,
                                      colorPalette.p400,
                                  )}`,
                              },
                              '& .MuiSwitch-track': tintedTrack,
                          },
                      }),
            };
        },
    },
};
